<template>
  <div id="app">
    <Loader v-if="isAuthenticated || isLoading || isCheckingStatus"></Loader>
    <router-view v-else />
    <doc-frame v-if="popup.visible" />
  </div>
</template>
<script>
import { mapGetters, mapActions } from 'vuex';
import Cookies from 'js-cookie';
import userServices from '@/services/user';
import Loader from '@/layout/Loader.vue';
import { DASHBOARD } from '@/config/urls';
import {
 addSearchParams,
 replaceUrlKeepParams,
 removeParams,
 getDeviceId,
} from '@/utils/common';
import getPageTitle from '@/utils/getPageTitle';
import oauth2Services from '@/services/oauth2';

const DocFrame = () => import('@/components/commons/DocFrame.vue');
export default {
  components: {
    Loader,
    DocFrame,
  },
  data() {
    return {
      isLoading: false,
      dashboardUrl: DASHBOARD,
      clearUpdateResize: '',
    };
  },
  computed: {
    ...mapGetters('auth', ['callbackUrl', 'ticket', 'twebmail', 'tprarams', 'isAuthenticated', 'routePath', 'accessTokenWebmail', 'isCheckingStatus']),
    ...mapGetters('docframe', ['popup']),
  },
  created() {
    if (this.routePath?.query?.bfc_aff) {
      Cookies.set('bfc_aff', this.routePath?.query?.bfc_aff, { domain: '.bizflycloud.vn', expires: 60 });
    }
  },
  async beforeMount() {
    this.updateCheckingStatus(true);
    this.checkStatus();
  },
  mounted() {
    this.updateDeviceWidth(window.innerWidth);
    window.addEventListener('resize', () => {
      clearTimeout(this.clearUpdateResize);
      this.clearUpdateResize = setTimeout(() => {
        this.updateDeviceWidth(window.innerWidth);
      }, 100);
    });
    this.$watch(
      '$i18n.locale',
      (newLocale, oldLocale) => {
        if (newLocale === oldLocale) {
          return;
        }
        this.changeLanguage(newLocale);
      },
    );
  },
  destroyed() {
    clearTimeout(this.clearUpdateResize);
  },
  methods: {
    ...mapActions('auth', [
      'updateAuthenticated',
      'updateTicket',
      'updateTwebmail',
      'updateAccessTokenWebmail',
      'updateCheckingStatus',
      'updateGoogleAccountInfo',
    ]),
    ...mapActions('device', ['updateDeviceWidth']),
    checkStatus() {
      const params = new URLSearchParams(window.location.search);
      const hasToken = params.has('tmptk');
      const isGoogleLogin = params.has('google_login');
      if (hasToken && isGoogleLogin) {
        this.handleGetDeviceId(params.get('tmptk'));
      } else {
        this.updateCheckingStatus(false);
        this.initData();
      }
    },
    async handleGetDeviceId(token) {
      try {
        const res = await getDeviceId();
        this.getInfoGoogleAccount(token, res);
      } catch (error) {
        console.log(error);
      }
    },
    async getInfoGoogleAccount(token, deviceId) {
      try {
        const decodedToken = decodeURIComponent(token);
        const data = {
          device_id: deviceId,
        };
        const res = await oauth2Services.getInfoGoogleAccount(decodedToken, data);
        this.updateGoogleAccountInfo(res);
        if (res?.type === 'register' && !window.location.href.includes('/register')) {
          replaceUrlKeepParams('/register');
        }
        if (res?.type === 'login' && !window.location.href.includes('/login')) {
          replaceUrlKeepParams('/login');
        }
        setTimeout(() => {
          this.updateCheckingStatus(false);
        }, 500);
      } catch (error) {
        this.updateCheckingStatus(false);
        removeParams();
        console.error(error);
      }
    },
    async initData() {
      this.isLoading = true;
      await new Promise((r) => setTimeout(r, 300)); // Wait midleware call
      const isLogout = localStorage.getItem('authenticated');
      try {
        const payload = {
          service: this.callbackUrl || '',
        };
        if (this.tprarams === 'webmail') {
          payload.t = 'webmail';
        }
        let isAuthenticated = false;
        const userRes = await userServices.userInfo(payload);
        isAuthenticated = userRes?.status_code === 200;
        // Set ticket
        if (userRes?.data?.ticket) {
          this.updateTicket(userRes?.data?.ticket);
        }
        if (userRes?.data?.login_param) {
          this.updateTwebmail(userRes?.data?.login_param);
        }
        if (userRes?.data?.access_token) {
          this.updateAccessTokenWebmail(userRes?.data?.access_token);
        }
        this.updateAuthenticated(isAuthenticated);
        if (isAuthenticated && isLogout !== 'logout') {
          const urlRedirect = this.callbackUrl || this.dashboardUrl;
          let nextUrl = addSearchParams(urlRedirect, { ticket: this.ticket });
          nextUrl = this.twebmail
          ? addSearchParams(nextUrl, {
            login_param: this.twebmail,
            access_token: this.accessTokenWebmail,
          })
          : nextUrl;
          window.location.href = nextUrl.href;
        }
        localStorage.setItem('authenticated', '');
      } catch (error) {
        console.error(error);
      } finally {
        await new Promise((r) => setTimeout(r, 300));
        this.isLoading = false;
      }
    },
    async changeLanguage(lang) {
      // setDocumentLang
      document.documentElement.lang = lang;
      // setDocumentTitle
      const title = getPageTitle(this.$route?.meta?.title);
      document.title = title;
    },
  },
};
</script>
